import { AbstractType, EnvironmentProviders, InjectionToken, Provider, Type, makeEnvironmentProviders } from '@angular/core';

/**
 * Asociación entre el contrato de un servicio de dominio y una implementación.
 *
 * Cada dominio declara su contrato como clase abstracta (o `InjectionToken`) y dos implementaciones:
 * la real, que consulta la API, y la mock, que lee fixtures de `src/testing`. Los componentes
 * inyectan siempre el contrato y nunca saben qué implementación reciben.
 */
export interface DataSourceBinding<T = unknown> {
  readonly provide: AbstractType<T> | InjectionToken<T>;
  readonly useClass: Type<T>;
}

/** Crea una asociación verificando que la implementación cumpla el contrato. */
export function dataSource<T>(provide: AbstractType<T> | InjectionToken<T>, useClass: Type<T>): DataSourceBinding<T> {
  return { provide, useClass };
}

/**
 * Elige las implementaciones a proveer: las reales siempre, y las mock por encima solo si `mockData` es `true`.
 * Una implementación mock reemplaza a la real del mismo contrato porque se registra después.
 */
export function selectDataSources(
  mockData: boolean,
  real: readonly DataSourceBinding[],
  mock: readonly DataSourceBinding[],
): EnvironmentProviders {
  const bindings = mockData ? [...real, ...mock] : real;
  const providers: Provider[] = bindings.map(({ provide, useClass }) => ({ provide, useClass }));
  return makeEnvironmentProviders(providers);
}
